<script setup>
import { toRefs } from "vue";
import moment from "moment";
import InfoHolder from "@/components/global/drawer/InfoHolder.vue";

const props = defineProps({
  beneficiary: {
    type: Object,
  },
});

const { beneficiary } = toRefs(props);

const formatDate = (date) => {
  return moment(date, "YYYY-MM-DD").format("DD-MM-YYYY");
};

const getGreekLevel = (languages) => {
  const greek = languages.find((lang) => lang.id === 52);

  if (greek) {
    return greek.level === 1
      ? "Basic"
      : greek.level === 2
      ? "Intermediate"
      : greek.level === 3
      ? "Advanced"
      : greek.level === 4
      ? "Native"
      : "None";
  }
  return "None";
};

const getEnglishLevel = (languages) => {
  const english = languages.find((lang) => lang.id === 40);

  if (english) {
    return english.level === 1
      ? "Basic"
      : english.level === 2
      ? "Intermediate"
      : english.level === 3
      ? "Advanced"
      : english.level === 4
      ? "Native"
      : "None";
  }
  return "None";
};

const getLangLevel = (level) => {
  return level === 1
    ? "Basic"
    : level === 2
    ? "Intermediate"
    : level === 3
    ? "Advanced"
    : level === 4
    ? "Native"
    : "None";
};

const getDob = (date) => {
  const dob = moment(date, "YYYY-MM-DD");
  const years = moment().diff(dob, "years");

  return `${dob.format("DD-MM-YYYY")} (Age: ${years})`;
};

const getCourses = (courses) => {
  const names = courses.map((course) => course.name);

  return names.join(", ");
};

const getJobs = (jobs) => {
  const names = jobs.map((job) => job.name);

  return names.join(", ");
};
</script>

<template>
  <div v-if="Object.keys(beneficiary).length > 0" class="pa-5">
    <h1>{{ beneficiary.fullName }}</h1>
    <v-divider />
    <div class="d-flex box-holder">
      <div class="d-flex box-row flex-1-0-0">
        <div class="box-container mt-5">
          <h3 class="pa-2">
            <v-icon>mdi-account</v-icon> Personal Information
          </h3>
          <InfoHolder title="Full Name" :info="beneficiary.fullName" />
          <InfoHolder
            title="Registration Date"
            :info="formatDate(beneficiary.registrationDate)"
          />
          <InfoHolder
            v-if="beneficiary.referSource"
            title="How did you learn about us?"
            :info="beneficiary.referSource.name"
          />

          <InfoHolder title="Telephone" :info="beneficiary.telephone" />

          <InfoHolder title="Email" :info="beneficiary.email" />

          <InfoHolder
            v-if="beneficiary.contactWay"
            title="What is your preferable way of contact?"
            :info="beneficiary.contactWay?.name"
          />
        </div>
        <div class="box-container mt-5">
          <h3 class="pa-2">
            <v-icon>mdi-chart-areaspline</v-icon> Demographics
          </h3>

          <InfoHolder title="CV" info="" />

          <InfoHolder title="Gender" :info="beneficiary.gender?.name" />

          <InfoHolder
            title="Date of Birth"
            :info="getDob(beneficiary.birthDate)"
          />

          <InfoHolder
            v-if="beneficiary.country"
            title="Country of Origin"
            :info="beneficiary.country?.name"
          />

          <InfoHolder
            v-if="beneficiary.city"
            title="City"
            :info="beneficiary.city"
          />

          <InfoHolder
            title="Are you a Greek citizen?"
            :info="beneficiary.greekCitizen"
            type="boolean"
          />

          <InfoHolder
            title="Do you have an unemployment card? (OAED card)"
            :info="beneficiary.unemploymentCard"
            type="boolean"
          />

          <InfoHolder
            title="Do you have regular contact with other organizations?"
            :info="beneficiary.otherOrganizations"
            type="boolean"
          />

          <InfoHolder
            v-if="beneficiary.planToStayDuration"
            title="How long do you plan to stay in Greece?"
            :info="beneficiary.planToStayDuration?.name"
          />
        </div>

        <div class="box-container mt-5">
          <h3 class="pa-2"><v-icon>mdi-translate</v-icon> Languages</h3>
          <div class="info pa-2">
            <p>
              <span class="label mb-1">English Level</span>
              <span class="answer">
                {{ getEnglishLevel(beneficiary.languages) }}
              </span>
            </p>
          </div>

          <div class="info pa-2">
            <p>
              <span class="label mb-1">Greek Level</span>
              <span class="answer">
                {{ getGreekLevel(beneficiary.languages) }}
              </span>
            </p>
          </div>

          <div class="info pa-2">
            <p>
              <span class="label mb-1">Other Languages</span>
            </p>
            <div v-for="lang in beneficiary.languages" :key="lang.id">
              <span v-if="lang.id != 40 && lang.id != 52" class="answer">
                {{ lang.name }} - {{ getLangLevel(lang.level) }}
              </span>
            </div>
          </div>
        </div>

        <div class="box-container mt-5">
          <h3 class="pa-2">
            <v-icon>mdi-file-document-outline</v-icon> Permit Data
          </h3>

          <InfoHolder
            v-if="beneficiary.legalStatus"
            title="Legal Status"
            :info="beneficiary.legalStatus.name"
          />

          <InfoHolder
            v-if="beneficiary.permitType"
            title="Type of Permit"
            :info="beneficiary.permitType.name"
          />

          <InfoHolder
            title="Do you have a TAX registration number? (AFM)"
            :info="beneficiary.hasAfm"
            type="boolean"
          />

          <InfoHolder
            title="Do you have a social security number? (AMKA)"
            :info="beneficiary.hasAmka"
            type="boolean"
          />

          <InfoHolder
            title="Do you have temporary social security number (PAAYPA)?"
            :info="beneficiary.hasPaaypa"
            type="boolean"
          />

          <InfoHolder
            title="Do you have social security employee number (EFKA or AMA)"
            :info="beneficiary.hasAma"
            type="boolean"
          />

          <InfoHolder
            title="Do you have a Greek bank account?"
            :info="beneficiary.hasGreekBankAccount"
            type="boolean"
          />
        </div>
      </div>

      <div class="d-flex box-row flex-1-0-0">
        <div class="box-container mt-5">
          <h3 class="pa-2">
            <v-icon>mdi-application-edit</v-icon> Beneficiary Status
          </h3>
          <InfoHolder
            v-if="beneficiary.status"
            title="Beneficiary Status"
            :info="beneficiary.status"
          />
          <InfoHolder
            v-if="beneficiary.branch.title"
            title="Odyssea Branch"
            :info="beneficiary.branch.title"
          />
          <InfoHolder
            v-if="beneficiary.vulnerabilityType"
            title="Vulnerability Type"
            :info="beneficiary.vulnerabilityType?.name"
          />
        </div>

        <div class="box-container mt-5">
          <h3 class="pa-2">
            <v-icon>mdi-comment-multiple</v-icon> Feedback and Comments
          </h3>
          <InfoHolder
            v-if="beneficiary.academyComments"
            title="Academy Comments"
            :info="beneficiary.academyComments"
            type="richText"
          />
          <InfoHolder
            v-if="beneficiary.taComments"
            title="TA Comments"
            :info="beneficiary.taComments"
            type="richText"
          />
          <InfoHolder
            v-if="beneficiary.ecComments"
            title="EC Comments"
            :info="beneficiary.ecComments"
            type="richText"
          />
        </div>

        <div class="box-container mt-5">
          <h3 class="pa-2"><v-icon>mdi-school</v-icon> Academy</h3>
          <InfoHolder
            v-if="beneficiary.academyOfficer"
            title="Academy Officer"
            :info="beneficiary.academyOfficer.name"
            type="richText"
          />

          <div
            v-if="beneficiary.preregisteredCourses?.length > 0"
            class="info pa-2"
          >
            <p>
              <span class="label mb-1">Courses Pre-Registered</span>
              <span class="answer">
                {{ getCourses(beneficiary.preregisteredCourses) }}
              </span>
            </p>
          </div>

          <div
            v-if="beneficiary.registeredCourses?.length > 0"
            class="info pa-2"
          >
            <p>
              <span class="label mb-1">Courses Registered</span>
              <span class="answer">
                {{ getCourses(beneficiary.registeredCourses) }}
              </span>
            </p>
          </div>

          <div v-if="beneficiary.enrolledCourses?.length > 0" class="info pa-2">
            <p>
              <span class="label mb-1">Courses Enrolled</span>
              <span class="answer">
                {{ getCourses(beneficiary.enrolledCourses) }}
              </span>
            </p>
          </div>
        </div>

        <div v-if="beneficiary.employability" class="box-container mt-5">
          <h3 class="pa-2"><v-icon>mdi-briefcase</v-icon> Employability</h3>
          <InfoHolder
            v-if="beneficiary.employability.employabilityOfficer"
            title="Employability Officer"
            :info="beneficiary.employability.employabilityOfficer.name"
          />

          <InfoHolder
            v-if="beneficiary.employability.nextStep"
            title="What is their most appropriate next step?"
            :info="beneficiary.employability.nextStep.name"
          />

          <div
            v-if="beneficiary.employability.employabilities?.length > 0"
            class="info pa-2"
          >
            <p>
              <span class="label mb-1">Employability Services</span>
            </p>
            <div
              v-for="empl in beneficiary.employability.employabilities"
              :key="empl.id"
              class="mt-2"
            >
              <div v-if="empl.responsibleOfficer">
                <b>{{ empl.responsibleOfficer.name }}</b>
              </div>
              <div v-if="empl.service">
                {{ empl.service.name }}
              </div>
              <div v-if="empl.date">
                {{ formatDate(empl.date) }}
              </div>
              <v-divider class="border-opacity-100" />
            </div>
          </div>

          <div v-if="beneficiary.selectedJobs?.length > 0" class="info pa-2">
            <p>
              <span class="label mb-1">Selected Job Positions</span>
            </p>
            {{ getJobs(beneficiary.selectedJobs) }}
          </div>

          <div v-if="beneficiary.matchedJobs?.length > 0" class="info pa-2">
            <p>
              <span class="label mb-1">Matched Job Positions </span>
            </p>
            {{ getJobs(beneficiary.matchedJobs) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
h1 {
  font-size: 48px;
  color: rgb(var(--v-theme-primary));
}
.box-holder {
  gap: 20px;
  .box-row {
    flex-direction: column;
    .box-container {
      border: 1px solid rgb(var(--v-theme-primary));
      h3 {
        background-color: rgb(var(--v-theme-primary));
        color: white;
      }
      .info {
        .label {
          text-decoration: underline;
        }
        .answer {
          display: block;
          font-weight: bold;
        }
      }
    }
  }
}
</style>
